import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import {
  getScans,
  processScans,
  getScanFileUrl,
  deletePdf,
  getCoverSheetHistory,
  CoverSheetHistoryEntry,
} from "../services/scansApiService";
import {
  ChevronLeft,
  ChevronRight,
  Send,
  FileText,
  Trash,
  Menu,
  X,
} from "lucide-react";
import * as signalR from "@microsoft/signalr";

// Ensure this path matches where you saved the spreadsheet component
import Spreadsheet from "../components/Spreadsheet";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfFile {
  name: string;
  url: string;
}

const ScansPage = () => {
  const { branch, warehouse } = useParams<{
    branch: string;
    warehouse: string;
  }>();

  // Upload State
  const [includeCoverSheet, setIncludeCoverSheet] = useState(true);

  // Grid State
  const [pdfFiles, setPdfFiles] = useState<PdfFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [numPages, setNumPages] = useState<number[]>([]);
  const [pageNumber, setPageNumber] = useState<number[]>([]);
  const [totalLoads, setTotalLoads] = useState(0);
  const [selectedDate, setSelectedDate] = useState("");
  const [sheetData, setSheetData] = useState<Record<string, any>[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [history, setHistory] = useState<CoverSheetHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = async () => {
    if (!branch || !warehouse) return;
    setHistoryLoading(true);
    try {
      const data = await getCoverSheetHistory(branch, warehouse);
      setHistory(data);
    } catch (error) {
      console.error("History fetch error:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchPdfs = async (showLoading = true) => {
    if (!branch || !warehouse) return;
    if (showLoading) setLoading(true);
    try {
      const pdfNames = await getScans(branch, warehouse);
      const files: PdfFile[] = [];
      for (const name of pdfNames) {
        const url = await getScanFileUrl(branch, warehouse, name);
        files.push({ name, url });
      }
      setPdfFiles(files);
      setNumPages(new Array(files.length).fill(0));
      setPageNumber(new Array(files.length).fill(1));
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (!branch || !warehouse) return;

    fetchPdfs();
    fetchHistory();

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_BASE_URL}/hubs/scans`)
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    // Register handler BEFORE starting — only once
    conn.on("ScansUpdated", () => {
      console.log("Scans Updated, fetching pdfs...");
      fetchPdfs();
    });

    // Re-join room on every reconnect + fetch anything missed
    conn.onreconnected(() => {
      console.log("Reconnected, re-joining room...");
      conn.invoke("JoinRoom", "scans", branch, warehouse);
      fetchPdfs();
    });

    conn
      .start()
      .then(() => {
        conn.invoke("JoinRoom", "scans", branch, warehouse);
      })
      .catch((err) => console.error("SignalR Connection Error: ", err));

    return () => {
      conn.stop();
    };
  }, [branch, warehouse]);

  const onDocumentLoadSuccess = (
    index: number,
    { numPages }: { numPages: number },
  ) => {
    setNumPages((prev) => {
      const newNumPages = [...prev];
      newNumPages[index] = numPages;
      return newNumPages;
    });
  };

  const handlePush = async () => {
    if (!branch || !warehouse || !selectedDate) return;
    const note = window.prompt("Add a note for this batch (optional):");
    if (note === null) return; // user cancelled
    setIsProcessing(true);
    try {
      const coversheetPayload = sheetData
        .filter((row) => {
          const loadsVal = parseFloat(row.loads);
          return !isNaN(loadsVal) && loadsVal > 0;
        })
        .map((row) => ({
          inventoryID: String(row.inventoryId || row.material || ""),
          vendorID: row.supplier || "",
          freightVendorID: row.hauler || "",
          loads: parseFloat(row.loads) || 0,
          receivedQty: parseFloat(row.qty) || 0,
          uom: row.uom || "",
        }));
      const payload = {
        branch,
        warehouse,
        coversheet: coversheetPayload,
        orderDate: selectedDate,
        ...(note.trim() && { note: note.trim() }),
      };
      const data = await processScans(payload);
      alert(`Successfully processed batch with ID: ${data.batchId}`);
      setSelectedDate("");
      fetchPdfs(false);
      fetchHistory();
    } catch (error: any) {
      console.error(
        "There has been a problem with your push operation:",
        error,
      );
      alert("Failed to process scans. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePushNoTickets = async () => {
    if (!branch || !warehouse || !selectedDate) return;
    if (!window.confirm(`Did you receive no tickets on ${selectedDate}`))
      return;
    setIsProcessing(true);
    try {
      const payload = {
        branch,
        warehouse,
        orderDate: selectedDate,
        coversheet: [
          {
            inventoryID: "No Material",
            vendorID: "N/A",
            freightVendorID: "N/A",
            loads: 0,
            receivedQty: 0,
            uom: "N/A",
          },
        ],
      };
      const data = await processScans(payload);
      alert("Sucessfully processed no tickets batch with ID: " + data.batchId);
      setSelectedDate("");
      fetchPdfs(false);
      fetchHistory();
    } catch (error: any) {
      console.error(
        "There has been a problem with your push operation:",
        error,
      );
      alert("Failed to process no tickets batch. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePushMissingTickets = async () => {
    if (!branch || !warehouse) return;
    if (
      !window.confirm(
        `Do you want to send ${totalPages} missing/rescanned tickets?`,
      )
    )
      return;
    setIsProcessing(true);
    try {
      const payload = {
        branch,
        warehouse,
        orderDate: "1999-01-01",
        coversheet: [],
      };
      const data = await processScans(payload);
      alert(
        "Sucessfully processed missing/rescanned tickets batch with ID: " +
          data.batchId,
      );
      fetchPdfs(false);
    } catch (error: any) {
      console.error(
        "There has been a problem with your push operation:",
        error,
      );
      alert("Failed to process no tickets batch. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (filename: string) => {
    if (branch && warehouse && window.confirm(`Delete ${filename}?`)) {
      setIsProcessing(true);
      try {
        const success = await deletePdf(branch, warehouse, filename);
        if (success) {
          setPdfFiles((prevFiles) =>
            prevFiles.filter((file) => file.name !== filename),
          );
          alert("Successfully deleted scan.");
        } else {
          alert("Failed to delete scan.");
        }
      } catch (error) {
        console.error(
          "There has been a problem with your delete operation:",
          error,
        );
        alert("Failed to delete scan. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const totalPages = numPages.reduce((acc, current) => acc + current, 0);

  return (
    <div className="flex h-screen overflow-hidden bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-white">

      {/* Sidebar toggle button - always visible top-left */}
      <button
        onClick={() => setSidebarOpen((o) => !o)}
        className="fixed top-3 left-3 z-[60] p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md text-slate-600 dark:text-slate-300 hover:text-primary transition-colors"
        title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Collapsible sidebar - overlays page */}
      <aside
        className={`fixed left-0 top-0 h-full z-50 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-14 flex items-center justify-center gap-2 px-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <FileText size={18} className="text-primary shrink-0" />
          <span className="font-bold text-slate-800 dark:text-slate-100 truncate">
            {branch} — {warehouse}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {historyLoading ? (
            <p className="text-sm text-slate-400 italic text-center mt-8">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center mt-8">No submissions yet</p>
          ) : (
            history.map((entry, i) => (
              // New tab so the plant's in-progress cover sheet and scans stay put
              <a
                key={i}
                href={`/batch-status/${encodeURIComponent(entry.batchId)}`}
                target="_blank"
                rel="noopener noreferrer"
                title="View batch status"
                className="block rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden hover:border-primary/50 hover:shadow-md transition-all"
              >
                {/* Card header */}
                <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                    {entry.orderDate}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 truncate ml-2">
                    {entry.batchId}
                  </span>
                </div>
                {/* Line items */}
                <div className="px-3 py-2 space-y-1">
                  {entry.lines.map((line, j) => (
                    <div key={j} className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-700 dark:text-slate-300 truncate">
                        {line.inventoryId}
                      </span>
                      <span className="ml-2 shrink-0 text-slate-500 dark:text-slate-400">
                        {line.loads} {line.loads === 1 ? "ticket" : "tickets"}
                      </span>
                    </div>
                  ))}
                </div>
              </a>
            ))
          )}
        </div>

        <div className="p-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-400 text-center">
          Last {history.length} submission{history.length !== 1 ? "s" : ""}
        </div>
      </aside>

      {/* Main Content: Split Grid */}
      <main className="flex-1 flex flex-col min-w-0 bg-slate-50 dark:bg-background-dark relative h-full">
        {/* --- TOP HALF: SPREADSHEET --- */}
        <div className="flex-1 overflow-hidden border-b border-slate-300 dark:border-slate-700 bg-white">
          <Spreadsheet
            storageKey={`spreadsheet_data_${branch}_${warehouse}`}
            onLoadsSumChange={setTotalLoads}
            onDataChange={setSheetData}
            selectedDate={selectedDate}
          />
        </div>

        {/* --- BOTTOM HALF: PDF HORIZONTAL SCROLL --- */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden p-6 bg-slate-50 dark:bg-background-dark relative">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="animate-pulse text-primary font-bold text-lg">
                Loading Scans...
              </div>
            </div>
          ) : (
            <div className="flex flex-row h-full gap-6 w-max pb-2">
              {pdfFiles.map((pdfFile, index) => (
                <div
                  key={index}
                  // min-w-0 is the magic key here. It prevents content from forcing the width wider than the aspect ratio.
                  className="group h-full aspect-[8.5/11] shrink-0 min-w-0 flex flex-col bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md hover:border-primary/30 transition-all"
                >
                  {/* Header - added min-w-0 so the truncate actually works */}
                  <div className="shrink-0 p-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between min-w-0 bg-white dark:bg-slate-900 rounded-t-xl">
                    <div className="flex items-center gap-2 overflow-hidden min-w-0">
                      <FileText className="text-red-500 shrink-0" size={18} />
                      <p className="text-sm font-semibold truncate text-slate-800 dark:text-slate-200">
                        {pdfFile.name}
                      </p>
                    </div>
                    <button
                      className="shrink-0 ml-2"
                      onClick={() => handleDelete(pdfFile.name)}
                    >
                      <Trash
                        className="text-red-500 hover:text-red-300"
                        size={18}
                      />
                    </button>
                  </div>

                  {/* PDF Container - Using the Absolute Position Trick */}
                  <a
                    href={pdfFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 relative bg-slate-100 dark:bg-slate-800 block hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    {/* Absolute inset locks the bounds to the parent flex-1 area, impossible to overflow */}
                    <div className="absolute inset-2 sm:inset-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white overflow-hidden pointer-events-none flex items-center justify-center">
                      <Document
                        file={pdfFile.url}
                        onLoadSuccess={(pdf) =>
                          onDocumentLoadSuccess(index, pdf)
                        }
                        loading={<div className="text-xs p-4">Loading...</div>}
                        className="w-full h-full flex items-center justify-center overflow-hidden"
                      >
                        <Page
                          pageNumber={pageNumber[index]}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          // Forces the canvas to never exceed 100% of the absolute container
                          className="flex items-center justify-center w-full h-full [&_canvas]:!w-auto [&_canvas]:!h-auto [&_canvas]:max-w-full [&_canvas]:max-h-full [&_canvas]:object-contain"
                        />
                      </Document>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5">
                      <span className="bg-white/90 dark:bg-slate-900/90 px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                        View Full PDF
                      </span>
                    </div>
                  </a>

                  {/* Footer */}
                  <div className="shrink-0 px-3 py-3 flex flex-col gap-3 bg-white dark:bg-slate-900 rounded-b-xl min-w-0">
                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 rounded-lg py-1 px-2">
                      <button
                        disabled={pageNumber[index] <= 1}
                        onClick={() =>
                          setPageNumber((prev) => {
                            const n = [...prev];
                            n[index]--;
                            return n;
                          })
                        }
                        className="text-slate-400 hover:text-primary disabled:opacity-20 shrink-0"
                      >
                        <ChevronLeft size={18} />
                      </button>

                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest truncate px-1">
                        Page {pageNumber[index]} / {numPages[index] || 1}
                      </span>

                      <button
                        disabled={pageNumber[index] >= numPages[index]}
                        onClick={() =>
                          setPageNumber((prev) => {
                            const n = [...prev];
                            n[index]++;
                            return n;
                          })
                        }
                        className="text-slate-400 hover:text-primary disabled:opacity-20 shrink-0"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Page Footer */}
        <footer className="h-12 flex items-center justify-between border-t border-slate-300 px-6 shrink-0 z-10 sticky bottom-0 bg-white dark:bg-slate-900">
          <div className="flex gap-6 items-center">
            <p className="text-lg font-bold text-slate-500 dark:text-slate-400">
              {totalPages} Tickets
            </p>
            <p className="text-lg font-bold text-slate-500 dark:text-slate-400">
              {totalLoads} Loads
            </p>
            {/* <label htmlFor="batchDate" className="text-sm font-bold text-slate-600 dark:text-slate-400">
                                Date:
                            </label> */}
            <input
              id="batchDate"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-slate-300 dark:border-slate-600 rounded-md px-3 py-1 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#107c41] transition-shadow"
            />
          </div>

          <div className="flex items-center gap-4">
            {/* 
            <button
              disabled={totalPages === 0 || isProcessing}
              onClick={handlePushMissingTickets}
              className="flex gap-2 items-center bg-[#107c41] hover:bg-[#0c5e31] text-white disabled:bg-slate-300 disabled:text-slate-500 px-6 py-1.5 rounded-md shadow-sm transition-colors font-bold"
            >
              <Send size={18} />
              Rescanned/Missing Tickets
            </button>
            */}

            <button
              disabled={totalPages !== 0 || !selectedDate || isProcessing}
              onClick={handlePushNoTickets}
              className="flex gap-2 items-center bg-[#107c41] hover:bg-[#0c5e31] text-white disabled:bg-slate-300 disabled:text-slate-500 px-6 py-1.5 rounded-md shadow-sm transition-colors font-bold"
            >
              <Send size={18} />
              No Tickets
            </button>
            <button
              // Button is disabled unless tickets equal loads, and we have at least 1 ticket
              disabled={
                totalPages !== totalLoads ||
                totalPages === 0 ||
                isProcessing ||
                !selectedDate
              }
              onClick={handlePush}
              className="flex gap-2 items-center bg-[#107c41] hover:bg-[#0c5e31] text-white disabled:bg-slate-300 disabled:text-slate-500 px-6 py-1.5 rounded-md shadow-sm transition-colors font-bold"
            >
              Send To Accounting
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="flex items-center justify-center w-8 h-8 rounded-full border border-slate-300 dark:border-slate-600 text-slate-500 hover:text-[#107c41] hover:border-[#107c41] transition-colors font-bold text-sm"
              title="Help"
            >
              ?
            </button>
          </div>
        </footer>

        {showHelp && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowHelp(false)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                Support
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
                For help, contact:
              </p>
              <p className="text-base font-semibold text-slate-900 dark:text-white">
                Noah Willen
              </p>
              <p className="text-base text-slate-700 dark:text-slate-300">
                (910) 818-7712
              </p>
              <button
                onClick={() => setShowHelp(false)}
                className="mt-5 w-full py-2 bg-[#107c41] hover:bg-[#0c5e31] text-white font-bold rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ScansPage;
